//rewrite all these useXStatusQuery as just useStatusQuery
//move nav throttle to general constants
//page c, module f

import { Share } from 'react-native'
import { router } from 'expo-router'

import { useAction, useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'

import { useStore as useMobXStore } from '@/models'
import { create } from 'zustand'
import { events } from '@/models/analytics/analytics-events'
import { formatPlayDate, formatIsoDateString } from '@/utils/dateFormatter'
import { formatMs} from '@/utils/timeFormatter'

import { useThrottledCallback } from 'use-debounce'

const { GAME_NAME, NAVIGATION_THROTTLE_MS, ROUND_COUNT } = GAME_CONSTANTS.POCKET_PICK_CONSTANTS

import { defaultTheme, purpleTheme, greenTheme, defaultImageTheme, purpleImageTheme, greenImageTheme } from "@/components/Mini/theme"

type StoreState = {
    GAME_NAME: string
    ROUND_COUNT: number
    playedDate: string
    setPlayedDate: () => void
    theme: typeof defaultTheme
    setTheme: (themeName: string) => void
}

const useGameStore = create<StoreState> ((set, get) => ({
  GAME_NAME,
  ROUND_COUNT,
  playedDate: formatPlayDate(),
  setPlayedDate: () => set({ playedDate: formatPlayDate() }),
  theme: greenImageTheme,
  setTheme: (themeName) => {
    if (themeName === 'default') {
      set({ theme: defaultTheme})
    } else if (themeName === 'purple'){
      set({ theme: purpleTheme })
    } else if (themeName === 'green'){
      set({ theme: greenTheme })
    } else if (themeName == 'defaultImage'){
      set({theme: defaultImageTheme})
    } else if (themeName == 'purpleImage'){
      set({theme: purpleImageTheme})
    } else if (themeName == 'greenImage'){
      set({theme: greenImageTheme})
    }
  }
}))



function useStore<T>(selector: (s: StoreState) => T): T {
  return useGameStore(selector)
}

function usePocketPickUser() {
  const {
    session: { user, isAdmin, isSuperAdmin },
    analytics: { setEvent }
  } = useMobXStore()

  return {
    userId: user?.auth0Id ?? null,
    userName: user?.name ?? null,
    isAdmin,
    isSuperAdmin,
    setEvent
  }
}

function usePocketPickQueryStatus() {
  const { userId } = usePocketPickUser()
  const playedDate = useStore((state) => state.playedDate)

  const status = useQuery(api.pocketPick.queries.getPocketPickStatus, userId ? { userId, playedDate } : 'skip')

  return { status }
}

function usePocketPickQueryLeaderboard() {
  const playedDate = useStore((state) => state.playedDate)

  const leaderboard = useQuery(api.pocketPick.queries.getPocketPickLeaderboard, { playedDate })

  return { leaderboard }
}

function usePocketPickQueryLog() {
  const { userId } = usePocketPickUser()

  const log = useQuery(api.pocketPick.queries.getPocketPickHistory, userId ? { userId } : 'skip')

  return { log }
}

function usePocketPickQueryHistory() {
  const { userId } = usePocketPickUser()
  const playedDate = useStore((state) => state.playedDate)

  const stats = useQuery(
    api.pocketPick.queries.getPocketPickProfileStats,
    userId ? { userId, todayDate: playedDate } : 'skip'
  )

  return { stats }
}

function usePocketPickQueryDiamonds(){
  const category="pocket_pick"
  const playedDate = useStore((state) => state.playedDate)
  const diamondEvents = useQuery(api.diamonds.queries.getDiamondEvents, {
    category,
    refId: playedDate
  });

  return {
    events: diamondEvents?.events,
    diamonds: diamondEvents?.total ?? 0
  }
}



//admin handlers
function useHandleTestingReset() {
  const { userId } = usePocketPickUser()
  const playedDate = useStore((state) => state.playedDate)
  const resetPocketPickGame = useMutation(api.pocketPick.mutations.resetPocketPickGameForTesting)

  const handleTestingReset = async () => {
    if (!userId) return
    await resetPocketPickGame({ userId, playedDate })
  }

  return handleTestingReset
}

function useHandleSeedLeaderboard() {

  const playedDate = useStore((state) => state.playedDate)
  const seedLeaderboard = useMutation(api.pocketPick.mutations.seedPocketPickLeaderboard)

  const handleSeedLeaderboard = async () => {
    await seedLeaderboard({ count: 20, playedDate })
  }

  return handleSeedLeaderboard
}

function useHandleClearLeaderboardSeeds() {
  const playedDate = useStore((state) => state.playedDate)
  const clearLeaderboardSeeds = useMutation(api.pocketPick.mutations.clearPocketPickLeaderboardSeedData)

  const handleClearLeaderboardSeeds = async () => {
    await clearLeaderboardSeeds({ playedDate })
  }

  return handleClearLeaderboardSeeds
}

function useHandleGenerateDeal() {
  const playedDate = useStore((state) => state.playedDate)
  const generateDeal = useAction(api.pocketPick.actions.generatePocketPickDealForTesting)

  const handleGenerateDeal = async () => {
    await generateDeal({ date: playedDate })
  }

  return generateDeal
}

  
  

//nav handlers

function useHandleModule({ event }: { event?: string } = {}) {
  const { userId, setEvent } = usePocketPickUser()
  const playedDate = useStore((state) => state.playedDate)

  const handleModule = useThrottledCallback(() => {
    if (event) {
      setEvent(event, {
        userId,
        playedDate
      })
    }

    router.push('/(root)/pocketPick')
  }, NAVIGATION_THROTTLE_MS)

  return handleModule
}

function useHandleClose() {
  const handleClose = useThrottledCallback(() => {
    router.back()
  }, NAVIGATION_THROTTLE_MS)

  return handleClose
}

function useHandleHome() {
  const handleHome = useThrottledCallback(() => {
    router.dismissTo('/(root)/pocketPick')
  }, NAVIGATION_THROTTLE_MS)

  return handleHome
}

function useHandlePlay() {
  const { userId, setEvent } = usePocketPickUser()
  const playedDate = useStore((state) => state.playedDate)
  const { status } = usePocketPickQueryStatus()

  const handlePlay = useThrottledCallback(() => {
    let event
    if (status?.status === 'completed') {
      event = events.pocketPick.replay_tapped
    } else if (status?.status === 'in_progress') {
      event = events.pocketPick.resume_tapped
    } else {
      event = events.pocketPick.play_tapped
    }

    setEvent(event, {
      userId,
      playedDate
    })

    router.push({ pathname: '/(root)/pocketPick/game' })
  }, NAVIGATION_THROTTLE_MS)

  return handlePlay
}

function useHandleResults() {
  const { userId, setEvent } = usePocketPickUser()
  const playedDate = useStore((state) => state.playedDate)

  const handleResults = useThrottledCallback(() => {
    setEvent(events.pocketPick.results_tapped, {
      userId,
      playedDate
    })
    router.push({ pathname: '/(root)/pocketPick/results' })
  }, NAVIGATION_THROTTLE_MS)

  return handleResults
}

function useHandleLeaderboard() {
  const { userId, setEvent } = usePocketPickUser()
  const playedDate = useStore((state) => state.playedDate)

  const handleLeaderboard = useThrottledCallback(() => {
    setEvent(events.pocketPick.leaderboard_tapped, {
      userId,
      playedDate
    })
    router.push('/(root)/pocketPick/leaderboard')
  }, NAVIGATION_THROTTLE_MS)

  return handleLeaderboard
}

function useHandleLog() {
  const { userId, setEvent } = usePocketPickUser()

  const playedDate = useStore((state) => state.playedDate)

  const handleLog = useThrottledCallback(() => {
    setEvent(events.pocketPick.profile_history_tapped, {
      userId,
      playedDate
    })
    router.navigate('/(root)/pocketPick/log')
  }, NAVIGATION_THROTTLE_MS)

  return handleLog
}

//handlers
function useHandleShare() {
    const { userId, setEvent } = usePocketPickUser()
    const playedDate = useStore((state) => state.playedDate)
    const gameName = useStore(s => s.GAME_NAME)
    const { status } = usePocketPickQueryStatus()

    const handleShare = async () => {
      if (status?.status !== 'completed') return

      const { roundsCorrect, totalRounds, roundResults, totalElapsedMs } = status

      //const whiteSquare = '\u{2B1C}'
      const redSquare = '\u{1F7E5}'
      const greenSquare = '\u{1F7E9}'

      const squares = roundResults.map((r) => r.correct ? greenSquare : redSquare).join('')

      const timeStr = totalElapsedMs != null ? ` in ${formatMs(totalElapsedMs)}` : ''
      const dateStr = formatIsoDateString(playedDate)
  
      const shareText =
        `${gameName} - ${dateStr}\n` +
        `${squares}\n` +
        `Rounds: ${roundsCorrect}/${totalRounds}${timeStr}\n` +
        `\n` +
        `Play at pokerpower.com`
      
      setEvent(events.pocketPick.results_share_tapped, {
        userId,
        playedDate,
        roundsCorrect,
        totalHands: totalRounds
      })

      try {
        await Share.share({ message: shareText })
      } catch {
        // User cancelled
      }
    }

    return handleShare
}


function useIsPerfect(){
  let isPerfect = false;

  const { status } = usePocketPickQueryStatus()

  if (status?.status === "completed"){
    if (status?.roundsCorrect === status?.totalRounds){
      if (status?.roundsCorrect > 0){
        isPerfect = true;
      }
    }

  }
  return isPerfect;
}

export type { StoreState }
export {
  //useGameStore,
  useStore, //zustand vars stable across pages
  usePocketPickUser, //eventually useCurrentUser as a general hook and separate call to useStore for setEvents
  usePocketPickQueryStatus, //reusable query with params
  usePocketPickQueryStatus as useQueryStatus, //move these all to no namespace
  usePocketPickQueryLeaderboard, //
  usePocketPickQueryLog, //
  usePocketPickQueryHistory, //
  usePocketPickQueryDiamonds, //
  useHandleTestingReset, //admin handler
  useHandleSeedLeaderboard, //
  useHandleClearLeaderboardSeeds, //
  //useHandleGenerateDeal, //
  useHandleModule,
  useHandleClose, //nav handler
  useHandleHome, //
  useHandlePlay, //
  useHandleResults, //
  useHandleLeaderboard, //
  useHandleLog, //
  useHandleShare, // other handler
  useIsPerfect
}
